import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { QueueProvider } from './contexts/QueueContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import AuthGuard from './components/AuthGuard';
import BottomNavigation from './components/BottomNavigation';
import SyncStatus from './components/SyncStatus';
import LoginScreen from './pages/LoginScreen';
import ProjectsScreen from './pages/ProjectsScreen';
import PhasePickerScreen from './pages/PhasePickerScreen';
import PhaseScreen from './pages/PhaseScreen';
import SyncScreen from './pages/SyncScreen';
import HubSpotDealsPage from './pages/HubSpotDealsPage';
import { applyTestIDs } from './utils/testUtils';
import './assets/index.css';

function App() {
  // Test-IDs für E2E-Tests anwenden
  useEffect(() => {
    // Beim ersten Rendern und dann alle 2 Sekunden (für dynamisch hinzugefügte Elemente)
    applyTestIDs();
    const interval = setInterval(applyTestIDs, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <QueueProvider>
            <ToastProvider>
              <AppLayout />
            </ToastProvider>
          </QueueProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// Layout-Komponente mit Bottom-Navigation
const AppLayout: React.FC = () => {
  const location = useLocation();
  const appVersion = __APP_BUILD_ID__;

  // Bottom-Navigation nur auf geschützten Screens anzeigen (nicht auf Login).
  // Auf einem aktiven Phasen-Screen ausblenden, weil die PhaseFooterNav (Issue #16)
  // dort die Navigation übernimmt und die globale BottomNav sonst doppelt Platz frisst.
  const isPhaseScreen = /^\/projects\/[^/]+\/phase\/[^/]+/.test(location.pathname);
  const showBottomNav = location.pathname !== '/login' && !isPhaseScreen;

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {showBottomNav && (
        <div className="fixed left-1/2 top-2 z-50 -translate-x-1/2 rounded bg-black/60 px-2 py-0.5 text-[10px] text-white md:left-auto md:right-2 md:translate-x-0">
          v:{appVersion}
        </div>
      )}

      <Routes>
        {/* Öffentliche Route */}
        <Route path="/login" element={<LoginScreen />} />
        
        {/* Geschützte Routen */}
        <Route path="/projects" element={
          <AuthGuard>
            <ProjectsScreen />
          </AuthGuard>
        } />
        
        {/* PhasePicker-Route */}
        <Route path="/projects/:id" element={
          <AuthGuard>
            <PhasePickerScreen />
          </AuthGuard>
        } />
        
        <Route path="/projects/:projectId/phase/:phaseId" element={
          <AuthGuard>
            <PhaseScreen />
          </AuthGuard>
        } />
        
        {/* Sync-Screen */}
        <Route path="/sync" element={
          <AuthGuard>
            <SyncScreen />
          </AuthGuard>
        } />
        
        {/* HubSpot Deals Screen */}
        <Route path="/hubspot-deals" element={
          <AuthGuard>
            <HubSpotDealsPage />
          </AuthGuard>
        } />

        {/* Fallback-Route */}
        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>
      
      {/* Bottom Navigation - nur auf geschützten Screens */}
      {showBottomNav && <BottomNavigation />}
      
      {/* Sync Status Bar - wird von SyncStatus selbst gesteuert */}
      {showBottomNav && <SyncStatus />}
    </div>
  );
};

export default App;
